/**
 * Upgrade Trigger Engine
 * Smart system that determines WHEN and HOW to show upgrade prompts
 * Based on user behavior, engagement, and psychological readiness
 */

export interface TriggerContext {
  type: 'milestone' | 'high-value' | 'pattern' | 'forecast' | 'social-proof' | 'curiosity';
  priority: 'low' | 'medium' | 'high';
  data?: any;
}

export interface UserEngagementData {
  checkInsCompleted: number;
  streakDays: number;
  lastRTIScore: number;
  avgRTIScore: number;
  daysActive: number;
  hasSeenForecasts: boolean;
  hasSeenPatterns: boolean;
  dismissedTeases: string[];
  featureInterests: string[];
}

/**
 * Analyzes user behavior and returns upgrade trigger recommendations
 */
export function analyzeUpgradeTriggers(): TriggerContext[] {
  const triggers: TriggerContext[] = [];
  const engagement = getUserEngagementData();

  // 1. Milestone Triggers (highest conversion)
  if (engagement.streakDays === 7 && !hasDismissed('milestone-7day')) {
    triggers.push({
      type: 'milestone',
      priority: 'high',
      data: { milestone: '7-day', streakDays: 7 }
    });
  }

  if (engagement.streakDays === 14 && !hasDismissed('milestone-14day')) {
    triggers.push({
      type: 'milestone',
      priority: 'high',
      data: { milestone: '14-day', streakDays: 14 }
    });
  }

  if (engagement.streakDays === 30 && !hasDismissed('milestone-30day')) {
    triggers.push({
      type: 'milestone',
      priority: 'high',
      data: { milestone: '30-day', streakDays: 30 }
    });
  }

  // Low tension achievement
  if (engagement.lastRTIScore < 30 && engagement.lastRTIScore > 0 && !hasDismissed('milestone-low-tension')) {
    triggers.push({
      type: 'milestone',
      priority: 'medium',
      data: { milestone: 'low-tension', tensionScore: engagement.lastRTIScore }
    });
  }

  // Improvement milestone
  const improvement = calculateImprovement();
  if (improvement > 30 && !hasDismissed('milestone-improvement')) {
    triggers.push({
      type: 'milestone',
      priority: 'high',
      data: { milestone: 'improvement', improvement }
    });
  }

  // 2. High-Value Moments (when user needs features most)
  if (engagement.lastRTIScore > 70 && !hasDismissed('high-tension-tease')) {
    triggers.push({
      type: 'high-value',
      priority: 'high',
      data: { 
        context: 'high-tension',
        tensionScore: engagement.lastRTIScore
      }
    });
  }

  // 3. Pattern Detection Opportunities
  if (engagement.checkInsCompleted >= 5 && !engagement.hasSeenPatterns && !hasDismissed('pattern-tease')) {
    triggers.push({
      type: 'pattern',
      priority: 'medium',
      data: { context: 'pattern-detected' }
    });
  }

  // 4. Forecast Availability
  if (engagement.checkInsCompleted >= 3 && !engagement.hasSeenForecasts && !hasDismissed('forecast-tease')) {
    triggers.push({
      type: 'forecast',
      priority: 'medium',
      data: { context: 'forecast' }
    });
  }

  // 5. Social Proof (subtle, ongoing)
  if (shouldShowSocialProof()) {
    triggers.push({
      type: 'social-proof',
      priority: 'low',
      data: {}
    });
  }

  // 6. Curiosity Triggers (for engaged users)
  if (engagement.checkInsCompleted >= 7 && !hasDismissed('streak-tease')) {
    triggers.push({
      type: 'curiosity',
      priority: 'low',
      data: { 
        context: 'streak',
        streakDays: engagement.streakDays 
      }
    });
  }

  return triggers.sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
}

/**
 * Get user engagement data from localStorage
 */
function getUserEngagementData(): UserEngagementData {
  const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
  const dismissedTeases = getDismissedTeases();
  const featureInterests = JSON.parse(localStorage.getItem('tension_feature_interests') || '[]');

  // Calculate streak
  const sortedCheckins = checkins
    .map((c: any) => new Date(c.timestamp))
    .sort((a: Date, b: Date) => b.getTime() - a.getTime());

  let streakDays = 0;
  if (sortedCheckins.length > 0) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let currentDate = new Date(today);
    for (const checkinDate of sortedCheckins) {
      const checkinDay = new Date(checkinDate);
      checkinDay.setHours(0, 0, 0, 0);
      
      if (checkinDay.getTime() === currentDate.getTime()) {
        streakDays++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else if (checkinDay.getTime() < currentDate.getTime()) {
        break;
      }
    }
  }

  // Get latest RTI score
  const lastCheckin = checkins[checkins.length - 1];
  const lastRTIScore = lastCheckin?.rtiScore || 0;

  // Calculate average RTI
  const rtiScores = checkins.map((c: any) => c.rtiScore || 0);
  const avgRTIScore = rtiScores.length > 0
    ? rtiScores.reduce((sum: number, score: number) => sum + score, 0) / rtiScores.length
    : 0;

  // Days active
  const firstCheckin = checkins[0];
  const daysActive = firstCheckin
    ? Math.ceil((Date.now() - new Date(firstCheckin.timestamp).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return {
    checkInsCompleted: checkins.length,
    streakDays,
    lastRTIScore,
    avgRTIScore,
    daysActive,
    hasSeenForecasts: localStorage.getItem('tension_has_seen_forecasts') === 'true',
    hasSeenPatterns: localStorage.getItem('tension_has_seen_patterns') === 'true',
    dismissedTeases,
    featureInterests
  };
}

/**
 * Calculate improvement from first week to recent scores
 */
function calculateImprovement(): number {
  const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
  if (checkins.length < 5) return 0;

  const firstWeek = checkins.slice(0, 7).map((c: any) => c.rtiScore || 0);
  const lastWeek = checkins.slice(-7).map((c: any) => c.rtiScore || 0);

  const firstAvg = firstWeek.reduce((sum: number, score: number) => sum + score, 0) / firstWeek.length;
  const lastAvg = lastWeek.reduce((sum: number, score: number) => sum + score, 0) / lastWeek.length;

  return Math.round(firstAvg - lastAvg);
}

/**
 * Check if user has dismissed a specific tease
 */
function hasDismissed(teaseId: string): boolean {
  const dismissed = localStorage.getItem(`tension_dismissed_tease_${teaseId}`);
  if (!dismissed) return false;

  const dismissedTime = parseInt(dismissed);
  const hoursSince = (Date.now() - dismissedTime) / (1000 * 60 * 60);
  
  // Re-show after 7 days for most teases, 30 days for milestones
  const cooldownHours = teaseId.startsWith('milestone') ? 24 * 30 : 24 * 7;
  return hoursSince < cooldownHours;
}

/**
 * Get all dismissed teases
 */
function getDismissedTeases(): string[] {
  const dismissed: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('tension_dismissed_tease_')) {
      dismissed.push(key.replace('tension_dismissed_tease_', ''));
    }
  }
  return dismissed;
}

/**
 * Determine if social proof should be shown
 */
function shouldShowSocialProof(): boolean {
  const dismissed = localStorage.getItem('tension_social_proof_dismissed');
  if (dismissed) return false;

  // Show after 3 check-ins
  const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
  return checkins.length >= 3;
}

/**
 * Get the best trigger to show right now
 */
export function getBestTrigger(): TriggerContext | null {
  const triggers = analyzeUpgradeTriggers();
  
  // Don't overwhelm users - limit to one trigger per session
  const sessionShown = sessionStorage.getItem('tension_upgrade_shown_this_session');
  if (sessionShown) return null;

  // Return highest priority trigger
  return triggers.length > 0 ? triggers[0] : null;
}

/**
 * Mark that we've shown an upgrade prompt this session
 */
export function markUpgradeShown(): void {
  sessionStorage.setItem('tension_upgrade_shown_this_session', 'true');
}

/**
 * Check if user has shown upgrade interest
 */
export function hasUpgradeInterest(): boolean {
  return localStorage.getItem('tension_upgrade_interest') === 'true';
}

/**
 * Track feature interest
 */
export function trackFeatureInterest(feature: string): void {
  const interests = JSON.parse(localStorage.getItem('tension_feature_interests') || '[]');
  if (!interests.includes(feature)) {
    interests.push(feature);
    localStorage.setItem('tension_feature_interests', JSON.stringify(interests));
  }
}
